import React from 'react';
import { fmtMoney, fmtPct, fmtDelta, fmtRate, fmtMinutes, asOf } from '../utils/priceMath';
import { rateSetLabel, opsRowsView, priceProvenanceLabel } from '../utils/assembliesManagerPricing';

/**
 * Cost & price card for AssembliesManagerPage (F02, F04, F07, F20, F22).
 *
 * Everything printed here comes from `model` (buildLocalCardModel /
 * normalizePricingResponse) and `totals` (perSetTotals) — the card does no
 * arithmetic of its own, so an operator can reproduce every dollar from the
 * line tables it renders. Per-set basis throughout: 1 set = ppq × primary +
 * components + purchased + the assembly's operations.
 */
export default function AssembliesManagerPricingCard({
  model, totals, drift, pricingStatus, onReload, ppq, ppqUnsaved, assemblyWeight, reloading,
}) {
  if (!model || !totals) return null;
  const t = totals;
  const status = pricingStatus || {};
  const isServer = model.source === 'server';
  const opsEst = t.operations.is_estimate;
  const dash = (v) => fmtMoney(v, { dash: true });
  const rates = rateSetLabel(model.rates_used);
  const manLines = model.manufactured.lines || [];
  const purLines = model.purchased.lines || [];
  const lq = model.last_quote;
  const sys = model.system_price || {};

  const freshness = isServer
    ? `live · GET /assemblies/${model.assembly_id}/pricing ${asOf(model.computed_at || status.fetchedAt) || ''}`.trim()
    : 'stored part rollups + saved assembly-ops cost (pricing endpoint unavailable)';

  const costFormula = [
    `manufactured ${fmtMoney(t.manufactured.cost_live)}`,
    `purchased ${fmtMoney(t.purchased.cost)}`,
    `operations ${fmtMoney(t.operations.cost)}`,
    t.coating_adjustment_live > 0 ? `− coating moved to ops ${fmtMoney(t.coating_adjustment_live)}` : null,
  ].filter(Boolean).join(' + ').replace('+ −', '−') + ` = ${fmtMoney(t.cost_live)}`;
  const excludedSteps = t.manufactured.primary_excluded_steps || [];

  const priceFormula = t.price_calc === null
    ? null
    : `manufactured ${fmtMoney(t.manufactured.price_calc)} + purchased at cost × (1 + markup) ${fmtMoney(t.purchased.suggested_price)} + operations ${fmtMoney(t.operations.price)} = ${fmtMoney(t.price_calc)}`;

  const priceUnavailableReason = (() => {
    if (t.price_calc !== null) return null;
    if (opsEst) return 'n/a while the operations grid has unsaved edits — Save Changes to reprice';
    if (!isServer) return 'n/a — needs GET /assemblies/{id}/pricing (part prices are computed server-side by the quote engine)';
    if (t.manufactured.price_missing_lines > 0) return `n/a — ${t.manufactured.price_missing_lines} part line(s) could not be priced (see line table)`;
    if (t.operations.price === null) return 'n/a — assembly operations have no price';
    return 'n/a';
  })();

  return (
    <div className="card mb-3 border-success">
      <div className="card-header bg-success bg-opacity-10 fw-bold py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>
          <i className="bi bi-cash-coin me-2"></i>
          Cost &amp; price — per set
          <span className="fw-normal text-muted small ms-2">
            (1 set = ppq {ppq} × primary + components + purchased + operations{ppqUnsaved && <span className="badge bg-warning text-dark ms-1">ppq unsaved</span>})
          </span>
        </span>
        <span className="d-flex align-items-center gap-2">
          <span className={`badge ${isServer ? 'bg-success' : 'bg-secondary'}`} title={freshness}>
            {isServer ? 'live' : 'stored + ops'}
          </span>
          {status.status === 'loading' && <span className="badge bg-light text-dark border">loading…</span>}
          {status.status === 'error' && <span className="badge bg-danger" title={status.error}>pricing error</span>}
          {onReload && (
            <button type="button" className="btn btn-sm btn-outline-success py-0" onClick={onReload} disabled={!!reloading}>
              {reloading ? 'Reloading…' : 'Reload'}
            </button>
          )}
        </span>
      </div>
      <div className="card-body py-2">
        <div className="small text-muted mb-2">
          {freshness}
          {isServer && model.server_formulas && model.server_formulas.cost && (
            <span className="ms-2" title={[
              `cost (live): ${model.server_formulas.cost}`,
              `cost (stored): ${model.server_formulas.cost_stored || ''}`,
              `price: ${model.server_formulas.price || ''}`,
              `book: ${model.server_formulas.book || ''}`,
            ].join('\n')}>· server formulas ⓘ</span>
          )}
        </div>
        {status.status === 'unavailable' && (
          <div className="alert alert-secondary py-1 px-2 small mb-2">
            <code>GET /assemblies/{model.assembly_id}/pricing</code> is not available on this API yet — the quotable price and last-quote rows need it.
            Shop cost below uses the stored part rollups and the saved assembly-operations calculation.
          </div>
        )}
        {status.status === 'error' && (
          <div className="alert alert-danger py-1 px-2 small mb-2">Pricing lookup failed: {status.error}. Showing the stored-rollup fallback.</div>
        )}
        {isServer && t.operations.error && !opsEst && (
          <div className="alert alert-danger py-1 px-2 small mb-2">
            The server could not price the assembly operations ({t.operations.error}) — the operations leg below is <strong>$0.00 by omission</strong>, not a real cost. Shop cost and quotable price are understated until it is fixed.
          </div>
        )}
        {isServer && t.manufactured.error_lines > 0 && (
          <div className="alert alert-warning py-1 px-2 small mb-2">
            {t.manufactured.error_lines} part line{t.manufactured.error_lines === 1 ? '' : 's'} could not be priced by the engine — the stored rollup stands in for cost and the quotable price is n/a (see the per-line table).
          </div>
        )}
        {(drift || []).length > 0 && (
          <div className="alert alert-warning py-1 px-2 small mb-2">
            <strong>Server per-set figures do not match their own line tables</strong> — the card prints the line-table sums:
            <ul className="mb-0">{drift.map((d, i) => <li key={i}>{d}</li>)}</ul>
          </div>
        )}

        <table className="table table-sm align-middle mb-2">
          <thead className="table-light">
            <tr>
              <th style={{ width: '22%' }}>Figure</th>
              <th style={{ width: '12%' }}>Basis</th>
              <th className="text-end" style={{ width: '12%' }}>Per set</th>
              <th>Derivation / source</th>
            </tr>
          </thead>
          <tbody>
            {/* ---- Shop cost (live) ---- */}
            <tr className="table-info">
              <td><strong>Shop cost (live)</strong></td>
              <td><span className="badge bg-dark">COST</span> <span className="text-muted small">per set</span></td>
              <td className="text-end fw-bold">{dash(t.cost_live)}</td>
              <td className="small">
                <div className="font-monospace">{costFormula}</div>
                {t.cost_stored !== null && t.cost_live !== null && Math.abs(t.cost_stored - t.cost_live) > 0.005 && (
                  <div className="text-muted">
                    stored rollups give {fmtMoney(t.cost_stored)} ({fmtDelta(t.cost_live - t.cost_stored)} live vs stored;{' '}
                    {t.manufactured.stale_lines} stale part line{t.manufactured.stale_lines === 1 ? '' : 's'})
                  </div>
                )}
                {t.manufactured.live_fallbacks > 0 && (
                  <div className="text-muted">{t.manufactured.live_fallbacks} part line(s) have no live rollup — stored total used for them</div>
                )}
              </td>
            </tr>
            <tr className="small">
              <td className="ps-4 text-muted">Manufactured parts ({t.manufactured.lines} line{t.manufactured.lines === 1 ? '' : 's'})</td>
              <td className="text-muted">Σ unit cost (live) × qty</td>
              <td className="text-end">{dash(t.manufactured.cost_live)}</td>
              <td className="text-muted">
                stored {dash(t.manufactured.cost_stored)}
                {t.manufactured.stale_lines > 0 && <span className="badge bg-warning text-dark ms-2">{t.manufactured.stale_lines} stale</span>}
                {' '}· primary counted ppq × — see the Parts table
                {isServer && excludedSteps.length > 0 && (
                  <div>
                    live = quote engine per part; the primary is priced <strong>without</strong> the steps the operations line owns ({excludedSteps.join(', ')}) — the same gate the quote page applies
                  </div>
                )}
              </td>
            </tr>
            <tr className="small">
              <td className="ps-4 text-muted">Purchased components ({t.purchased.lines})</td>
              <td className="text-muted">Σ vendor cost × qty</td>
              <td className="text-end">{dash(t.purchased.cost)}</td>
              <td className="text-muted">
                preferred vendor cost, else highest vendor cost
                {t.purchased.below_cost_lines > 0 && <span className="badge bg-danger ms-2">{t.purchased.below_cost_lines} book price below cost</span>}
              </td>
            </tr>
            <tr className="small">
              <td className="ps-4 text-muted">Assembly operations</td>
              <td className="text-muted">{opsEst ? 'minutes × standard wage' : 'minutes × wages (90-day)'}</td>
              <td className="text-end">{dash(t.operations.cost)}</td>
              <td className="text-muted">
                {t.operations.coating_cost > 0 && <>of which outsourced coating {fmtMoney(t.operations.coating_cost)} · </>}
                {opsEst
                  ? <span className="badge bg-warning text-dark">unsaved — standard rate estimate</span>
                  : <>saved rows via POST /quotes/calculate-assembly-ops — see the operations table above</>}
              </td>
            </tr>
            {t.coating_adjustment > 0 && (
              <tr className="small">
                <td className="ps-4 text-muted">Coating moved to assembly operations</td>
                <td className="text-muted">− ppq × primary coating</td>
                <td className="text-end text-nowrap">
                  −{fmtMoney(t.coating_adjustment)}
                  <div>
                    <span className={`badge ${t.live_coating_excluded_by_engine ? 'bg-light text-dark border' : 'bg-info text-dark'}`}>
                      {t.live_coating_excluded_by_engine ? 'stored basis only' : 'taken off live'}
                    </span>
                  </div>
                </td>
                <td className="text-muted">
                  {t.live_coating_excluded_by_engine
                    ? <>
                        coating moved to assembly operations: −{fmtMoney(t.coating_adjustment)}. The primary's <em>stored</em> rollup contains its coating_cost_per_unit
                        and the assembly prices Coating on its own operations row; the quote engine's live figure for the primary already leaves that coating out,
                        so the live shop cost above subtracts nothing — only the stored basis ({dash(t.cost_stored)}) is corrected, once (F07)
                      </>
                    : <>
                        the primary part's stored rollup already contains its coating_cost_per_unit and the assembly prices Coating on its own row;
                        the quote engine strips the part-level coating in that case, so the card does too (F07)
                      </>}
                </td>
              </tr>
            )}

            {/* ---- Quotable price ---- */}
            <tr className="table-success">
              <td><strong>Quotable price at default rates</strong></td>
              <td><span className="badge bg-success">PRICE</span> <span className="text-muted small">per set</span></td>
              <td className="text-end fw-bold">{dash(t.price_calc)}</td>
              <td className="small">
                {priceFormula
                  ? <div className="font-monospace">{priceFormula}</div>
                  : <div className="text-muted">{priceUnavailableReason}</div>}
                <div className="text-muted">rates: {rates}</div>
                {t.margin_pct !== null && (
                  <div>margin vs shop cost <strong>{fmtPct(t.margin_pct)}</strong> = (price − cost) ÷ price</div>
                )}
              </td>
            </tr>
            {t.price_calc !== null && (
              <>
                <tr className="small">
                  <td className="ps-4 text-muted">Manufactured parts</td>
                  <td className="text-muted">Σ calculated price × qty</td>
                  <td className="text-end">{dash(t.manufactured.price_calc)}</td>
                  <td className="text-muted">POST /quotes/calculate-part-cost per part at the rates above (primary excludes stations the ops line owns)</td>
                </tr>
                <tr className="small">
                  <td className="ps-4 text-muted">Purchased components</td>
                  <td className="text-muted">Σ cost × (1 + markup) × qty</td>
                  <td className="text-end">{dash(t.purchased.suggested_price)}</td>
                  <td className="text-muted">book price is NOT used here (F23) — below-cost book prices would understate the quote</td>
                </tr>
                <tr className="small">
                  <td className="ps-4 text-muted">Assembly operations</td>
                  <td className="text-muted">Σ minutes × sell rate (+ coating × (1 + markup))</td>
                  <td className="text-end">{dash(t.operations.price)}</td>
                  <td className="text-muted">calculate-assembly-ops calculated_price</td>
                </tr>
              </>
            )}

            {/* ---- Book price ---- */}
            <tr>
              <td><strong>Book price</strong></td>
              <td><span className="badge bg-secondary">PRICE</span> <span className="text-muted small">per set</span></td>
              <td className="text-end">{dash(t.book_price)}</td>
              <td className="small text-muted">
                parts.price × qty + consumable book price × qty — set on quote approval (or typed on the Part page) — <strong>excludes operations</strong>.
                {t.book_price === null && ' No book price on any line.'}
                {t.book_price !== null && t.book_price_source === 'lines' && <> = parts {fmtMoney(t.manufactured.book_price)} + purchased {fmtMoney(t.purchased.book_price)}</>}
                {t.book_price !== null && t.book_price_source === 'server' && <> (server per_set.book_price — the line tables carry no book prices)</>}
              </td>
            </tr>

            {/* ---- System price ---- */}
            <tr>
              <td><strong>System Price</strong> <span className="text-muted small">(assembly_price)</span></td>
              <td><span className="badge bg-secondary">PRICE</span> <span className="text-muted small">per set</span></td>
              <td className="text-end">{dash(t.system_price)}</td>
              <td className="small">
                {t.system_price === null
                  ? <span className="text-muted">not set — the Quote page shows "Not set" and seeds no override</span>
                  : (
                    <>
                      <span className="badge bg-light text-dark border me-2">{priceProvenanceLabel({ source: sys.source, quoteId: sys.quote_id, setAt: sys.set_at })}</span>
                      {t.system_margin_pct !== null && (
                        <>margin vs shop cost <strong className={t.system_margin_pct < 0 ? 'text-danger' : ''}>{fmtPct(t.system_margin_pct)}</strong>
                          {t.system_margin_pct < 0 && <span className="badge bg-danger ms-2">below cost</span>}</>
                      )}
                    </>
                  )}
              </td>
            </tr>

            {/* ---- Last quoted ---- */}
            <tr>
              <td><strong>Last quoted</strong></td>
              <td><span className="badge bg-secondary">PRICE</span> <span className="text-muted small">per set</span></td>
              <td className="text-end">{lq ? dash(lq.override ?? lq.per_set_price) : '—'}</td>
              <td className="small">
                {!lq && (
                  <span className="text-muted">
                    {isServer ? 'no quote line references this assembly' : 'n/a — needs GET /assemblies/{id}/pricing'}
                  </span>
                )}
                {lq && (
                  <>
                    Quote #{lq.quote_id} <span className="badge bg-light text-dark border">{lq.status || '?'}</span>
                    {lq.sets ? <> · {lq.sets} set{lq.sets === 1 ? '' : 's'}</> : null}
                    {' '}· price/set {dash(lq.per_set_price)}
                    {lq.override != null && <> (override {fmtMoney(lq.override)}/set)</>}
                    {' '}· cost/set {dash(lq.per_set_cost)}
                    {lq.lines_without_cost > 0 && (
                      <span className="badge bg-warning text-dark ms-1" title="Quote lines with no stored cost_breakdown contribute $0 to cost/set">
                        {lq.lines_without_cost} line{lq.lines_without_cost === 1 ? '' : 's'} without stored cost
                      </span>
                    )}
                    {lq.per_set_price && lq.per_set_cost != null && (
                      <> · margin {fmtPct((Number(lq.per_set_price) - Number(lq.per_set_cost)) / Number(lq.per_set_price))}</>
                    )}
                    <div className="text-muted">
                      {lq.approved_at ? `approved ${new Date(lq.approved_at).toLocaleDateString()}` : (lq.created_at ? `created ${new Date(lq.created_at).toLocaleDateString()}` : '')}
                      {' '}· {lq.per_set_price_basis || 'quote-line Σ estimated_price × qty ÷ sets'}, at that quote's own rates (not the defaults above)
                    </div>
                  </>
                )}
              </td>
            </tr>

            <tr>
              <td><strong>Calculated weight</strong></td>
              <td><span className="text-muted small">BOM Σ</span></td>
              <td className="text-end">{assemblyWeight != null ? `${assemblyWeight} lb` : '—'}</td>
              <td className="small text-muted">GET /assemblies/{model.assembly_id}/weight</td>
            </tr>
          </tbody>
        </table>

        {/* Line-level "show the math" — only meaningful once the server prices the lines. */}
        {isServer && (manLines.length > 0 || purLines.length > 0) && (
          <details className="small">
            <summary className="text-muted" style={{ cursor: 'pointer' }}>Per-line derivation ({manLines.length} manufactured · {purLines.length} purchased · {(model.operations.rows || []).length} operations)</summary>
            <div className="table-responsive mt-2">
              <table className="table table-sm table-bordered mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Item</th><th>Role</th><th className="text-end">Qty/set</th>
                    <th className="text-end">Unit cost stored</th><th className="text-end">Unit cost live</th>
                    <th className="text-end">Unit price calc</th><th className="text-end">Ext cost (live)</th><th className="text-end">Ext price</th><th>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {manLines.map((l) => (
                    <tr key={`m-${l.part_id}`}>
                      <td className="font-monospace">{l.product_id || `part ${l.part_id}`}</td>
                      <td>{l.role}</td>
                      <td className="text-end">{l.qty_per_assembly}</td>
                      <td className="text-end">{dash(l.unit_cost_stored)}</td>
                      <td className="text-end">{dash(l.unit_cost_live)}</td>
                      <td className="text-end">{dash(l.unit_price_calc)}</td>
                      <td className="text-end">{l.unit_cost_live === null ? '—' : fmtMoney(l.unit_cost_live * l.qty_per_assembly)}</td>
                      <td className="text-end">{l.unit_price_calc === null ? '—' : fmtMoney(l.unit_price_calc * l.qty_per_assembly)}</td>
                      <td>
                        {l.stale === true && (
                          <span className="badge bg-warning text-dark me-1" title={l.stale_basis === 'rollup'
                            ? `stored total ≠ its own components (rollup ${fmtMoney(l.unit_cost_rollup)})`
                            : 'engine live cost ≠ stored total'}>stale</span>
                        )}
                        {l.stale === null && l.unit_cost_live !== null && (
                          <span className="badge bg-light text-dark border me-1" title="The engine priced this line only partly (ops-owned steps / coating stripped), so live ≠ stored is expected; the Parts table's rollup check judges staleness">partial</span>
                        )}
                        {l.error && <span className="badge bg-danger me-1" title={l.error}>error</span>}
                        {l.coating_priced_on_ops_line && <span className="badge bg-info text-dark me-1">coating on ops row</span>}
                        {(l.excluded_steps || []).length > 0 && <span className="text-muted">excludes {l.excluded_steps.join(', ')} (on ops line) </span>}
                        {l.cost_breakdown && l.cost_breakdown.breakdown && (
                          <span className="text-muted">
                            {l.cost_breakdown.breakdown.material && <>material {fmtMoney(l.cost_breakdown.breakdown.material.cost)} </>}
                            {l.cost_breakdown.breakdown.laser && <>· laser {fmtMoney(l.cost_breakdown.breakdown.laser.total_cost)} </>}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {purLines.map((l) => (
                    <tr key={`p-${l.consumable_id}`}>
                      <td className="font-monospace">{l.product_id || `consumable ${l.consumable_id}`}</td>
                      <td>purchased</td>
                      <td className="text-end">{l.qty_per_assembly}</td>
                      <td className="text-end">{dash(l.unit_cost)}</td>
                      <td className="text-end">{dash(l.unit_cost)}</td>
                      <td className="text-end">{dash(l.suggested_price)}</td>
                      <td className="text-end">{l.unit_cost === null ? '—' : fmtMoney(l.unit_cost * l.qty_per_assembly)}</td>
                      <td className="text-end">{l.suggested_price === null ? '—' : fmtMoney(l.suggested_price * l.qty_per_assembly)}</td>
                      <td>
                        <span className="text-muted">{l.cost_source}</span>
                        {l.below_cost && <span className="badge bg-danger ms-1">book {fmtMoney(l.book_price)} below cost</span>}
                      </td>
                    </tr>
                  ))}
                  {opsRowsView(model.operations.rows).map((r, i) => (
                    <tr key={`o-${i}`}>
                      <td className="font-monospace">{r.station}</td>
                      <td>operation</td>
                      <td className="text-end">{r.minutes === null ? '—' : fmtMinutes(r.minutes)}</td>
                      <td className="text-end">—</td>
                      <td className="text-end">{fmtMoney(r.cost)}</td>
                      <td className="text-end">{dash(r.price)}</td>
                      <td className="text-end">{fmtMoney(r.cost)}</td>
                      <td className="text-end">{dash(r.price)}</td>
                      <td className="text-muted">
                        {r.isOutsourced ? 'outsourced $' : <>{fmtRate(r.costRate)} cost · {fmtRate(r.priceRate)} sell</>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
